import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, rem, Text, VStack } from '@bonusly/design-system';

import { DirectReport } from '../../../../types/user';
import { DirectReportRow } from './direct-report-row';

// DirectReport enriched with team tracker recognition data
export interface EnrichedDirectReport extends Omit<DirectReport, 'lastRecognitionAt'> {
	lastRecognizedAt: number | null;
	recognitionCount: number;
}

interface DirectReportListProps {
	directReports: EnrichedDirectReport[];
}

export const DirectReportList: FC<DirectReportListProps> = ({ directReports }) => {
	const { t } = useTranslation(['navigation', 'tracker_widget']);

	const { recognizedThisMonth, notRecognizedThisMonth } = useMemo(() => {
		const recognized: EnrichedDirectReport[] = [];
		const notRecognized: EnrichedDirectReport[] = [];

		directReports.forEach((report) => {
			if (report.recognitionCount > 0) {
				recognized.push(report);
			} else {
				notRecognized.push(report);
			}
		});

		return { recognizedThisMonth: recognized, notRecognizedThisMonth: notRecognized };
	}, [directReports]);

	if (notRecognizedThisMonth.length === 0 && recognizedThisMonth.length === 0) {
		return null;
	}

	return (
		<VStack as="nav" aria-label={t('navigation:teamMenu.directReportsNavLabel')} gap={rem(16)} w="100%" alignItems="stretch">
			{notRecognizedThisMonth.length > 0 && (
				<Flex flexDirection="column" gap={rem(4)}>
					<SectionHeading>{t('tracker_widget:person_list.havent_recognized')}</SectionHeading>
					<VStack spacing={0} alignItems="stretch">
						{notRecognizedThisMonth.map((directReport) => (
							<DirectReportRow key={directReport.id} directReport={directReport} />
						))}
					</VStack>
				</Flex>
			)}

			{recognizedThisMonth.length > 0 && (
				<Flex flexDirection="column" gap={rem(4)}>
					<SectionHeading>{t('tracker_widget:person_list.recognized')}</SectionHeading>
					<VStack spacing={0} alignItems="stretch">
						{recognizedThisMonth.map((directReport) => (
							<DirectReportRow key={directReport.id} directReport={directReport} />
						))}
					</VStack>
				</Flex>
			)}
		</VStack>
	);
};

const SectionHeading: FC<{ children: React.ReactNode }> = ({ children }) => (
	<Text color="text.default" fontSize={100} fontWeight="semibold" lineHeight={6}>
		{children}
	</Text>
);
